import { Delete, Edit, GroupAdd } from '@mui/icons-material'
import {
	Button,
	IconButton,
	TableCell
} from '@mui/material'
import { action, runInAction } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { useEffect, useMemo } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { BaseGroup } from '../../../../api/DTO'
import { GroupReduced } from '../../../../api/DTOtemp'
import { GroupsClient } from '../../../../api/clients/identity'
import { DeleteDialog } from '../../../../components/dialogs/DeleteDialog'
import { FullscreenSpinner } from '../../../../components/feedback/circular'
import IconButtonLink from '../../../../components/links/IconButtonLink'
import { TableColumn } from '../../../../components/tables/DTO'
import { FilteredTable } from '../../../../components/tables/FilteredTable'
import { FilterControl } from '../../../../components/tables/filters/DTO'
import { toastService } from '../../../../services/notifications/ToastService'
import { makeChildRoute } from '../../../../utils/CreateChildRoute'
import AdministrationPageContainer from '../AdministrationPageContainer'
import { CreateGroupDialog } from './CreateGroupDialog'

export const GroupsPage = observer(() => {
	const groupsClient = new GroupsClient()
	const history = useHistory()

	const store = useLocalObservable(() => ({
		groups: undefined as BaseGroup[] | undefined,
		createGroupOpen: false,
		groupToDelete: undefined as BaseGroup | undefined,
	}))

	useEffect(() => {
		groupsClient.GetAllGroups().then(
			action((v) => {
				store.groups = v.data
			}),
		)
	}, [store])

	const handleShowCreateGroup = () => {
		store.createGroupOpen = true
	}

	const handleCloseCreateGroup = () => {
		store.createGroupOpen = false
	}

	const handleCreateGroup = async (group: GroupReduced) => {
		store.createGroupOpen = false

		groupsClient
			.CreateGroup(group)
			.then((response) => {
				const groupId = response.data.id
				history.push(`/_administration/_groups/${groupId}`)

				toastService.displayToast({
					message: 'Group created',
					area: 'global',
					severity: 'success',
				})
			})
			.catch(() => {
				toastService.displayToast({
					message: 'Error creating group',
					area: 'global',
					severity: 'error',
				})
			})
	}

	const handleDeleteClicked = action((group: BaseGroup) => {
		store.groupToDelete = group
	})

	const handleDeleteDialogClosed = action(() => {
		store.groupToDelete = undefined
	})

	const handleDeleteGroup = async (groupId: number) => {
		groupsClient.DeleteGroup(groupId)

		runInAction(() => {
			if (store.groups !== undefined) {
				const groupToDelete = store.groups.map((v) => v.id).indexOf(groupId)

				store.groups.splice(groupToDelete, 1)
			}
		})
	}

	const filterControls: FilterControl[] = useMemo(() => [
		{
			type: 'search',
			name: 'search',
			column: ["name", "description"]
		}
	], [store.groups])

	const url = useRouteMatch().url

	const columns: TableColumn<BaseGroup>[] = useMemo(() => [
		{
			name: "name",
			label: "Name"
		},
		{
			name: "description",
			label: "Description"
		},
		{
			renderHeading: <TableCell key={2} align="right">Actions</TableCell>,
			renderCell: (group: BaseGroup) =>
				<TableCell key={2} align="right">
					<IconButtonLink to={makeChildRoute(`${group.id}`, url)}>
						<Edit fontSize="small" />
					</IconButtonLink>
					<IconButton
						onClick={() => handleDeleteClicked(group)}
						size="large"
					>
						<Delete fontSize="small" />
					</IconButton>
				</TableCell >
		},
	], [])

	return (
		<>
			<AdministrationPageContainer
				title="Groups"
				actions={
					<div>
						<Button
							variant="contained"
							color="primary"
							startIcon={<GroupAdd />}
							onClick={handleShowCreateGroup}
						>
							Create Group
						</Button>
					</div>
				}
			>
				{store.groups === undefined ? <FullscreenSpinner /> : (
					<FilteredTable<BaseGroup>
						columns={columns}
						allEntries={store.groups}
						filterControls={filterControls}
					/>
				)}
			</AdministrationPageContainer>
			<CreateGroupDialog
				open={store.createGroupOpen}
				onClose={handleCloseCreateGroup}
				createGroup={handleCreateGroup}
				groupNames={store.groups?.map((v) => v.name) ?? []}
			/>
			{store.groupToDelete !== undefined && (
				<DeleteDialog<number>
					open={store.groupToDelete !== undefined}
					onClose={handleDeleteDialogClosed}
					itemId={store.groupToDelete.id}
					itemName={store.groupToDelete.name}
					onDelete={handleDeleteGroup}
				/>
			)}
		</>
	)
})
